/**
 * Permission utility functions for handling browser permissions
 */

export interface PermissionStatus {
  granted: boolean;
  status?: 'granted' | 'denied' | 'prompt';
  error?: string;
  coordinates?: { lat: number; long: number };
}

export interface AllPermissions {
  location: PermissionStatus;
  microphone: PermissionStatus;
  camera: PermissionStatus;
}

const PERMISSION_CACHE_KEY = 'ameen.permission.granted.cache.v1';

type PermissionCache = {
  location?: boolean;
  microphone?: boolean;
  camera?: boolean;
};

const isNativePlatform = (): boolean => {
  if (typeof window === 'undefined') return false;
  const capacitor = (window as any).Capacitor;
  return Boolean(capacitor?.isNativePlatform?.());
};

const readPermissionCache = (): PermissionCache => {
  try {
    const raw = localStorage.getItem(PERMISSION_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PermissionCache;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writePermissionCache = (next: PermissionCache): void => {
  try {
    localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(next));
  } catch {
    // ignore storage failures
  }
};

const resolveCachedPermission = (
  previous: boolean | undefined,
  latest: PermissionStatus
): boolean | undefined => {
  if (latest.granted || latest.status === 'granted') return true;
  if (latest.status === 'denied') return false;
  return previous;
};

const updatePermissionCacheFromResult = (permissions: AllPermissions): void => {
  const current = readPermissionCache();
  const merged: PermissionCache = {
    location: resolveCachedPermission(current.location, permissions.location),
    microphone: resolveCachedPermission(current.microphone, permissions.microphone),
    camera: resolveCachedPermission(current.camera, permissions.camera),
  };
  writePermissionCache(merged);
};

/**
 * Get permission status without requesting (where supported)
 */
export const getPermissionState = async (name: 'geolocation' | 'microphone' | 'camera'): Promise<'granted' | 'denied' | 'prompt'> => {
  try {
    if ('permissions' in navigator) {
      // Note: Some browsers might not support 'microphone' or 'camera' in the permissions API
      const result = await navigator.permissions.query({ name: name as PermissionName });
      return result.state;
    }
  } catch (e) {
    console.warn(`Permission query for ${name} not supported`);
  }
  return 'prompt';
};

/**
 * Request location permission
 */
export const requestLocationPermission = async (): Promise<PermissionStatus> => {
  const currentState = await getPermissionState('geolocation');
  if (currentState === 'granted') return { granted: true, status: 'granted' };
  if (currentState === 'denied') return { granted: false, status: 'denied', error: 'Location access denied by user' };

  try {
    if (!navigator.geolocation) {
      return { granted: false, error: 'Geolocation is not supported by this browser' };
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          granted: true,
          status: 'granted',
          coordinates: {
            lat: position.coords.latitude,
            long: position.coords.longitude
          }
        } as any),
        (error) => {
          let errorMessage = 'Location access denied';
          let status: 'denied' | 'prompt' = 'prompt';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              status = 'denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          resolve({ granted: false, status, error: errorMessage });
        },
        { timeout: 10000 }
      );
    });
  } catch (error) {
    return { granted: false, error: 'Failed to request location permission' };
  }
};

/**
 * Request microphone permission
 */
export const requestMicrophonePermission = async (): Promise<PermissionStatus> => {
  const currentState = await getPermissionState('microphone');
  if (currentState === 'granted') return { granted: true, status: 'granted' };

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    return { granted: true, status: 'granted' };
  } catch (error: any) {
    let errorMessage = 'Microphone access denied';
    let status: 'denied' | 'prompt' = 'prompt';
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      errorMessage = 'Microphone access denied by user';
      status = 'denied';
    } else if (error.name === 'NotFoundError') {
      errorMessage = 'No microphone found';
    } else if (error.name === 'NotSupportedError') {
      errorMessage = 'Microphone not supported';
    }
    return { granted: false, status, error: errorMessage };
  }
};

/**
 * Request camera permission
 */
export const requestCameraPermission = async (): Promise<PermissionStatus> => {
  const currentState = await getPermissionState('camera');
  if (currentState === 'granted') return { granted: true, status: 'granted' };

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(track => track.stop());
    return { granted: true, status: 'granted' };
  } catch (error: any) {
    let errorMessage = 'Camera access denied';
    let status: 'denied' | 'prompt' = 'prompt';
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      errorMessage = 'Camera access denied by user';
      status = 'denied';
    } else if (error.name === 'NotFoundError') {
      errorMessage = 'No camera found';
    } else if (error.name === 'NotSupportedError') {
      errorMessage = 'Camera not supported';
    }
    return { granted: false, status, error: errorMessage };
  }
};

/**
 * Request all permissions at once
 */
export const requestAllPermissions = async (): Promise<AllPermissions> => {
  const [location, microphone, camera] = await Promise.all([
    requestLocationPermission(),
    requestMicrophonePermission(),
    requestCameraPermission()
  ]);

  const all = { location, microphone, camera };
  updatePermissionCacheFromResult(all);
  return all;
};

/**
 * Check specifically if any permissions are denied and need manual intervention
 */
export const hasDeniedPermissions = (permissions: AllPermissions): boolean => {
  return (
    permissions.location.status === 'denied' ||
    permissions.microphone.status === 'denied' ||
    permissions.camera.status === 'denied'
  );
};

/**
 * Check if ALL required permissions are granted
 * For this app, microphone and camera are likely required for the AI interaction and document scanning
 */
export const arePermissionsGranted = (permissions: AllPermissions): boolean => {
  return permissions.microphone.granted && permissions.camera.granted;
};

/**
 * Check permission status without requesting
 */
export const getAllPermissionsStatus = async (): Promise<AllPermissions> => {
  const [locationState, microphoneState, cameraState] = await Promise.all([
    getPermissionState('geolocation'),
    getPermissionState('microphone'),
    getPermissionState('camera')
  ]);

  const browserStatus: AllPermissions = {
    location: { granted: locationState === 'granted', status: locationState },
    microphone: { granted: microphoneState === 'granted', status: microphoneState },
    camera: { granted: cameraState === 'granted', status: cameraState }
  };

  // Capacitor Android WebView may return "prompt" after app restart even when
  // Android permission is already granted. Reuse last known granted state to
  // keep UI status consistent across relaunches.
  if (isNativePlatform()) {
    const cached = readPermissionCache();
    if (browserStatus.location.status === 'prompt' && cached.location === true) {
      browserStatus.location = { granted: true, status: 'granted' };
    }
    if (browserStatus.microphone.status === 'prompt' && cached.microphone === true) {
      browserStatus.microphone = { granted: true, status: 'granted' };
    }
    if (browserStatus.camera.status === 'prompt' && cached.camera === true) {
      browserStatus.camera = { granted: true, status: 'granted' };
    }
  }

  updatePermissionCacheFromResult(browserStatus);

  return browserStatus;
};

/**
 * Subscribe to Permission API changes (best-effort; not all browsers fire for all names).
 */
export const subscribeToPermissionChanges = (onChange: () => void): (() => void) => {
  let aborted = false;
  const disposers: Array<() => void> = [];
  const names = ['geolocation', 'microphone', 'camera'] as const;

  names.forEach(name => {
    if (!('permissions' in navigator)) return;
    navigator.permissions
      .query({ name: name as PermissionName })
      .then(status => {
        if (aborted) return;
        const handler = () => onChange();
        status.addEventListener('change', handler);
        disposers.push(() => status.removeEventListener('change', handler));
      })
      .catch(() => {});
  });

  return () => {
    aborted = true;
    disposers.forEach(d => d());
  };
};
